import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, useScroll, useTransform, AnimatePresence, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest, tokenRequest } from "../../authConfig";
import { FEATURE_FLAGS } from "../../config/featureFlags";
import { safeLoginRedirect } from "../../utils/msalHelpers";
import { callMsGraph } from "../../graph";
import AuthLogger from "../../utils/AuthLogger";
import {
  Shield,
  Zap,
  ArrowRight,
  Ship,
  Brain,
  Network,
  Container,
  MessageCircle,
  Anchor,
  LogOut,
  User,
} from "lucide-react";
import Logo from '../UI/Logo';
import './LandingPage.css';
// KBC QUIZ IMPORTS (Temporary - Event Only)
import KBCEventBanner from '../KBCQuiz/KBCEventBanner';
// END KBC QUIZ IMPORTS

interface UserProfile {
  displayName: string;
  mail: string;
  jobTitle?: string;
  photo?: string;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});
  const shouldReduceMotion = useReducedMotion();
  const [currentQuery, setCurrentQuery] = useState(0);
  const [showHeaderButton, setShowHeaderButton] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const containerRef = useRef(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const sampleQueries = useMemo(() => [
    "How many active shipments are there for Nike?",
    "What are the top five destination countries by shipment count?",
    "Compare Carrier & Merchant Haulage ratio for inland shipments in Q2-2025.",
    "Which are the 5 trades that drove the most margin last quarter?",
  ], []);

  const pillarsData = useMemo(() => [
    {
      icon: Network,
      title: "All shipments. One conversation.",
      description: "Ocean and Inland unified through AI intelligence.",
      example: "Show margin by lane last quarter",
    },
    {
      icon: Brain,
      title: "Profitability, explained.",
      description: "AI-driven insights into margin drivers across all dimensions.",
      example: "Which trades are most profitable?",
    },
    {
      icon: Zap,
      title: "Faster confidence.",
      description: "From question to decision and beyond, expedited",
      example: "Show shipments at risk this week",
    },
    {
      icon: Shield,
      title: "Built for Maersk.",
      description: "Enterprise AI with security and compliance by design.",
      example: "Enterprise neural security",
    },
  ], []);

  // Load user profile when authenticated
  useEffect(() => {
    if (account && isAuthenticated) {
      setLoadingProfile(true);
      instance.acquireTokenSilent({
        ...tokenRequest,
        account: account
      }).then((response) => {
        if (response) {
          callMsGraph(response.accessToken).then((result) => {
            setUserProfile(result);
            setLoadingProfile(false);
          }).catch((error) => {
            console.error("Error fetching user profile:", error);
            setLoadingProfile(false);
          });
        }
      }).catch((error) => {
        console.error("Error acquiring token:", error);
        setLoadingProfile(false);
      });
    }
  }, [account, isAuthenticated, instance]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuery((prev) => (prev + 1) % sampleQueries.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [sampleQueries.length]);

  // Scroll detection to show/hide header button
  useEffect(() => {
    const handleScroll = () => {
      // Show header button when scrolled past 80% of viewport height (roughly past hero section)
      const heroSectionHeight = window.innerHeight * 0.8;
      const currentScrollY = window.scrollY;
      setShowHeaderButton(currentScrollY > heroSectionHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleStartExploring = useCallback(async () => {
    if (!FEATURE_FLAGS.REQUIRE_AUTHENTICATION) {
      navigate('/chat');
      return;
    }

    if (inProgress !== InteractionStatus.None) {
      console.log('Authentication interaction already in progress, skipping...', inProgress);
      return;
    }

    if (isAuthenticated) {
      // User is already logged in, navigate to chat
      navigate('/chat');
    } else {
      try {
        console.log('Initiating login...');
        // Trigger login - user will be redirected back to landing page after authentication
        await safeLoginRedirect(instance, loginRequest);
      } catch (error: any) {
        console.error("Login error:", error);
        if (error?.errorCode !== 'interaction_in_progress') {
          alert(`Login failed: ${error?.message || 'Unknown error'}. Please try again.`);
        }
      }
    }
  }, [isAuthenticated, navigate, instance, inProgress]);

  const handleLogout = useCallback(() => {
    const userEmail = userProfile?.mail || account?.username || 'unknown';
    AuthLogger.logLogout(userEmail);
    
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance, userProfile, account]);

  // Optimized animation variants
  const animationVariants = useMemo(() => ({
    // Reduced motion variants
    reducedMotion: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 0.3 }
    },
    // Full motion variants
    fullMotion: {
      fadeInUp: {
        initial: { y: 20, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        transition: { duration: 0.5 }
      },
      fadeInUpDelayed: {
        initial: { y: 30, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        transition: { duration: 0.6 }
      },
      cardHover: {
        y: -8,
        scale: 1.02,
        transition: { duration: 0.2 }
      },
      buttonHover: {
        scale: 1.05,
        boxShadow: "0 25px 50px rgba(66, 176, 213, 0.5)",
        background: "linear-gradient(135deg, #42B0D5, #3A9BC1, #2E8FA4)",
        transition: { duration: 0.2 }
      }
    }
  }), []);

  // Get appropriate variants based on motion preference
  const getVariants = useCallback((variantName: string) => {
    if (shouldReduceMotion) {
      return animationVariants.reducedMotion;
    }
    return animationVariants.fullMotion[variantName as keyof typeof animationVariants.fullMotion] || animationVariants.fullMotion.fadeInUp;
  }, [shouldReduceMotion, animationVariants]);

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gray-900 overflow-hidden"
    >
      {/* Sticky Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{ 
          willChange: 'transform, opacity',
          backgroundColor: showHeaderButton ? 'rgba(17, 24, 39, 0.95)' : 'transparent',
          backdropFilter: showHeaderButton ? 'blur(12px)' : 'none',
          borderBottom: showHeaderButton ? '1px solid rgba(75, 85, 99, 0.5)' : '1px solid transparent'
        }}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-4">
          {/* Logo Section - Always visible */}
          <div className="flex items-center flex-shrink-0 min-w-0">
            <Logo alwaysDark={true} />
          </div>
          
          {/* Right side - User Profile and/or Explore Button */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {/* User Profile - Show when authenticated */}
            {FEATURE_FLAGS.REQUIRE_AUTHENTICATION && isAuthenticated && (
              <motion.div
                ref={userMenuRef}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                {loadingProfile ? (
                  <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md border border-[#42B0D5]/30 rounded-full px-4 py-2">
                    <div className="w-8 h-8 bg-[#42B0D5]/20 rounded-full animate-pulse"></div>
                    <div className="w-24 h-4 bg-[#42B0D5]/20 rounded animate-pulse"></div>
                  </div>
                ) : (
                  <div 
                    className="flex items-center gap-3 bg-white/10 backdrop-blur-md border border-[#42B0D5]/30 rounded-full px-4 py-2 cursor-pointer hover:bg-white/15 transition-all duration-200"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                  >
                    {/* Profile Picture */}
                    {userProfile?.photo ? (
                      <img
                        src={userProfile.photo}
                        alt={userProfile.displayName}
                        className="w-8 h-8 rounded-full object-cover border-2 border-[#42B0D5]"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-[#42B0D5] to-[#5FC0E8] rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {(userProfile?.displayName || account?.name)?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )}
                    
                    {/* User Name */}
                    <span className="text-white font-medium text-sm hidden sm:block">
                      {userProfile?.displayName || account?.name || 'User'}
                    </span>
                    
                    {/* User icon for mobile */}
                    <User size={18} className="text-white sm:hidden" />
                  </div>
                )}
                
                {/* User Menu Dropdown */}
                <AnimatePresence>
                  {showUserMenu && !loadingProfile && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 top-full mt-2 min-w-64 max-w-96 w-max bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
                    >
                      {/* User Info */}
                      <div className="p-4 border-b border-gray-700 bg-gray-750">
                        <div className="flex items-center gap-3 mb-3">
                          {userProfile?.photo ? (
                            <img
                              src={userProfile.photo}
                              alt={userProfile.displayName}
                              className="w-12 h-12 rounded-full object-cover border-2 border-[#42B0D5]"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gradient-to-br from-[#42B0D5] to-[#5FC0E8] rounded-full flex items-center justify-center text-white font-bold text-lg">
                              {(userProfile?.displayName || account?.name)?.charAt(0).toUpperCase() || 'U'}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-semibold text-sm whitespace-normal break-words">
                              {userProfile?.displayName || account?.name || 'User'}
                            </p>
                            <p className="text-gray-400 text-xs whitespace-nowrap">
                              {userProfile?.mail || account?.username || ''}
                            </p>
                          </div>
                        </div>
                        {userProfile?.jobTitle && (
                          <p className="text-gray-300 text-xs whitespace-normal break-words">
                            {userProfile.jobTitle}
                          </p>
                        )}
                      </div>
                      
                      {/* Logout Button */}
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-3 text-left text-red-400 hover:bg-red-900/20 transition-colors flex items-center gap-2 text-sm font-medium"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
            
            {/* Explore Button - Only show on desktop when scrolled past hero */}
            <AnimatePresence>
              {showHeaderButton && (
                <motion.button
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  onClick={handleStartExploring}
                  disabled={inProgress !== InteractionStatus.None}
                  whileHover={shouldReduceMotion ? {} : {
                    scale: inProgress === InteractionStatus.None ? 1.05 : 1,
                    boxShadow: inProgress === InteractionStatus.None ? "0 10px 25px rgba(66, 176, 213, 0.3)" : "none"
                  }}
                  whileTap={shouldReduceMotion ? {} : { scale: inProgress === InteractionStatus.None ? 0.95 : 1 }}
                  className={`hidden md:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#42B0D5] to-[#42B0D5] hover:from-[#42B0D5] hover:to-[#5FC0E8] text-white rounded-full font-medium transition-all duration-300 shadow-lg ${
                    inProgress !== InteractionStatus.None ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                  style={{ 
                    boxShadow: "0 8px 20px rgba(66, 176, 213, 0.3)",
                    willChange: shouldReduceMotion ? 'auto' : 'transform'
                  }}
                >
                  <Anchor size={18} />
                  {inProgress !== InteractionStatus.None ? 'Loading...' : 
                   !FEATURE_FLAGS.REQUIRE_AUTHENTICATION ? 'Explore' :
                   isAuthenticated ? 'Continue to Chat' : 'Explore'}
                  <ArrowRight size={16} />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.header>

      {/* Floating Action Button - Mobile Only (Bottom Right) */}
      <AnimatePresence>
        {showHeaderButton && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={handleStartExploring}
            disabled={inProgress !== InteractionStatus.None}
            whileHover={shouldReduceMotion ? {} : {
              scale: inProgress === InteractionStatus.None ? 1.1 : 1,
              boxShadow: inProgress === InteractionStatus.None ? "0 15px 35px rgba(66, 176, 213, 0.4)" : "none"
            }}
            whileTap={shouldReduceMotion ? {} : { scale: inProgress === InteractionStatus.None ? 0.9 : 1 }}
            className={`fixed bottom-6 right-6 z-40 md:hidden flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-[#42B0D5] to-[#42B0D5] hover:from-[#42B0D5] hover:to-[#5FC0E8] text-white rounded-full font-medium transition-all duration-300 shadow-2xl ${
              inProgress !== InteractionStatus.None ? 'opacity-60 cursor-not-allowed' : ''
            }`}
            style={{ 
              boxShadow: "0 10px 25px rgba(66, 176, 213, 0.4)",
              willChange: shouldReduceMotion ? 'auto' : 'transform'
            }}
          >
            <Anchor size={20} className="flex-shrink-0" />
            <span className="text-sm font-semibold whitespace-nowrap">
              {inProgress !== InteractionStatus.None ? 'Loading...' : 
               !FEATURE_FLAGS.REQUIRE_AUTHENTICATION ? 'Explore' :
               isAuthenticated ? 'Continue' : 'Explore'}
            </span>
            <ArrowRight size={18} className="flex-shrink-0" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <motion.section
        style={{ y: heroY, opacity: heroOpacity }}
        className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black md:py-20"
      >
        {/* !!!!QUIZ pt-20 */}
        {/* AI-themed Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {!shouldReduceMotion && (
            <>
              {/* Neural Network Connections - Optimized count */}
              <svg className="absolute inset-0 w-full h-full opacity-20" style={{ willChange: 'auto' }}>
                {[...Array(6)].map((_, i) => (
                  <motion.line
                    key={i}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.3 }}
                    transition={{
                      duration: 1.5,
                      delay: i * 0.1,
                      repeat: Infinity,
                      repeatType: "reverse",
                      repeatDelay: 2,
                      ease: "easeInOut",
                    }}
                    x1={`${Math.random() * 100}%`}
                    y1={`${Math.random() * 100}%`}
                    x2={`${Math.random() * 100}%`}
                    y2={`${Math.random() * 100}%`}
                    stroke="#42B0D5"
                    strokeWidth="1"
                    className="opacity-40"
                  />
                ))}
              </svg>

              {/* AI Data Nodes - Reduced count and optimized */}
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 0.6 }}
                  transition={{
                    delay: i * 0.1,
                    duration: 1,
                    repeat: Infinity,
                    repeatType: "reverse",
                    repeatDelay: 1.5,
                    ease: "easeOut",
                  }}
                  className="absolute w-2 h-2 bg-[#42B0D5] rounded-full"
                  style={{
                    left: `${20 + i * 10}%`,
                    top: `${25 + Math.sin(i * 0.5) * 25}%`,
                    boxShadow: "0 0 10px #42B0D5, 0 0 20px #42B0D5",
                    willChange: 'transform, opacity',
                  }}
                />
              ))}

              {/* Floating AI Ship Icon - Optimized */}
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 0.1 }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                }}
                className="absolute top-20 right-20 hidden sm:block"
                style={{ willChange: 'transform' }}
              >
                <Ship size={60} className="text-[#42B0D5]" />
              </motion.div>

              {/* Container Processing Visual - Optimized */}
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute bottom-20 left-20 opacity-5 hidden sm:block"
                style={{ willChange: 'transform' }}
              >
                <Container size={50} className="text-[#42B0D5]" />
              </motion.div>
            </>
          )}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center max-w-5xl lg:max-w-6xl mx-auto px-4 sm:px-8 sm:pb-8">
          {/* KBC QUIZ EVENT BANNER (Temporary - Event Only) - moved to footer CTA */}

          <motion.h1
            {...(shouldReduceMotion ? getVariants('reduced') : {
              initial: { y: 30, opacity: 0 },
              animate: { y: 0, opacity: 1 },
              transition: { duration: 0.6, delay: 0.2 }
            })}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Navigate through shipments with{" "}
            <span className="text-[#42B0D5] relative glow-text">
              intelligence
              {!shouldReduceMotion && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="absolute bottom-0 left-0 w-full h-1 bg-[#42B0D5] origin-left"
                  style={{ boxShadow: "0 0 20px #42B0D5" }}
                />
              )}
            </span>
          </motion.h1>

          <motion.p
            {...(shouldReduceMotion ? getVariants('reduced') : {
              initial: { y: 20, opacity: 0 },
              animate: { y: 0, opacity: 1 },
              transition: { duration: 0.5, delay: 0.4 }
            })}
            className="text-lg sm:text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed glow-text-subtle"
          >
            Transform natural language queries into powerful shipment insights. Analyze, visualize, and optimize maritime operations with AI-powered intelligence.
          </motion.p>

          <motion.button
            {...(shouldReduceMotion ? getVariants('reduced') : {
              initial: { y: 20, opacity: 0 },
              animate: { y: 0, opacity: 1 },
              transition: { duration: 0.1, delay: 0.1 }
            })}
            whileHover={shouldReduceMotion ? {} : {
              scale: inProgress === InteractionStatus.None ? 1.05 : 1,
              boxShadow: inProgress === InteractionStatus.None ? "0 10px 25px rgba(66, 176, 213, 0.3)" : "none"
            }}
            whileTap={shouldReduceMotion ? {} : { scale: inProgress === InteractionStatus.None ? 0.95 : 1 }}
            onClick={handleStartExploring}
            disabled={inProgress !== InteractionStatus.None}
            className={`bg-gradient-to-r from-[#42B0D5] to-[#42B0D5] hover:from-[#42B0D5] hover:to-[#5FC0E8] text-white px-8 sm:px-12 py-3 sm:py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg flex items-center gap-3 mx-auto glow-button ${
              inProgress !== InteractionStatus.None ? 'opacity-60 cursor-not-allowed' : ''
            }`}
            style={{ 
              boxShadow: "0 8px 20px rgba(66, 176, 213, 0.3)",
              willChange: shouldReduceMotion ? 'auto' : 'transform'
            }}
          >
            <MessageCircle size={20} />
            {inProgress !== InteractionStatus.None ? 'Loading...' : 
             !FEATURE_FLAGS.REQUIRE_AUTHENTICATION ? 'Start Exploring' :
             isAuthenticated ? 'Continue to Chat' : 'Start Exploring'}
            <ArrowRight size={20} />
          </motion.button>
        </div>
      </motion.section>

      {/* Value Pillars */}
      <section className="py-16 sm:py-24 bg-gray-800 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 glow-text">
              AI-Powered Shipment Intelligence
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300">
              Harness the power of AI to transform shipment data into actionable insights
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillarsData.map((pillar, index) => (
              <motion.div
                key={index}
                {...(shouldReduceMotion ? getVariants('reduced') : {
                  initial: { y: 30, opacity: 0 },
                  whileInView: { y: 0, opacity: 1 },
                  transition: { 
                    duration: 0.4, 
                    delay: index * 0.1
                  }
                })}
                viewport={{ once: true }}
                whileHover={shouldReduceMotion ? {} : {
                  y: -8,
                  scale: 1.02,
                  transition: { duration: 0.1 }
                }}
                className="bg-gray-700 p-6 sm:p-8 rounded-2xl shadow-xl transition-all duration-300 group border border-gray-600 ai-card hover-glow-md"
                style={{ willChange: shouldReduceMotion ? 'auto' : 'transform' }}
              >
                <div className="mb-6">
                  <pillar.icon
                    size={40}
                    className="text-[#42B0D5] group-hover:text-[#3A9BC1] transition-colors glow-icon"
                  />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 glow-text-subtle">
                  {pillar.title}
                </h3>
                <p className="text-gray-300 mb-4 text-base">{pillar.description}</p>
                {/* {!shouldReduceMotion && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    whileHover={{ 
                      opacity: 1, 
                      height: "auto",
                      transition: { duration: 0.2, ease: "easeOut" }
                    }}
                    className="text-[#42B0D5] font-medium overflow-hidden text-sm"
                  >
                    AI Example: "{pillar.example}"
                  </motion.div>
                )} */}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Query Preview */}
      <section className="py-16 sm:py-24 bg-black scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 glow-text">
              Neural Intelligence in Action
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300">
              Experience the power of natural language sourced analytics for our global shipping operations
            </p>
          </motion.div>

          <div className="bg-gray-900 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-2xl border border-gray-700 ai-terminal">
            {/* AI Terminal Header */}
            <div className="bg-black rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-600">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-3 h-3 bg-red-500 rounded-full glow-dot"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full glow-dot"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full glow-dot"></div>
                <Ship size={18} className="text-[#42B0D5] ml-2" />
                <span className="text-gray-400 text-base ml-2">
                  Shipment CoCaptain
                </span>
              </div>

              <div className="font-mono text-[#42B0D5] text-base sm:text-lg">
                <span className="text-gray-500">AI{`>`}</span>{" "}
                <AnimatePresence mode="wait">
                  <motion.span
                    key={currentQuery}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className="glow-text-ai"
                  >
                    {sampleQueries[currentQuery]}
                  </motion.span>
                </AnimatePresence>
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="ml-1 text-[#42B0D5] glow-cursor"
                >
                  |
                </motion.span>
              </div>
            </div>

            {/* AI Processing Results */}
            <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
              {[
                { title: "Shipment Coverage", value: "6.5M+", color: "blue" },
                { title: "Revenue In View", value: "$36 Billion", color: "blue" },
                { title: "Inland Corridor Count", value: "62,000", color: "blue" },
              ].map((result, index) => (
                <motion.div
                  key={index}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    duration: 0.4,
                    delay: (currentQuery * 3 + index) * 0.08,
                    ease: "easeOut",
                  }}
                  className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-600 ai-result-card"
                >
                  <h4 className="text-gray-300 text-sm sm:text-base mb-2">{result.title}</h4>
                  <div
                    className={`text-xl sm:text-2xl font-bold ${
                      result.color === "green"
                        ? "text-green-400"
                        : result.color === "yellow"
                          ? "text-yellow-400"
                          : "text-[#42B0D5]"
                    } glow-text-result`}
                  >
                    {result.value}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Profitability Spotlight */}
      {/* <section className="py-16 sm:py-24 bg-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 glow-text">
              AI Profitability Intelligence
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300">
              Neural insights across customers, trades, and shipments
            </p>
          </motion.div>

          <div className="bg-gray-700 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl border border-gray-600 ai-dashboard"> */}
            {/* AI Filter Interface */}
            {/* <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-10">
              {(["customer", "trade", "shipment"] as const).map((view) => (
                <motion.button
                  key={view}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 ${
                    profitabilityView === view
                      ? "bg-[#42B0D5] text-white shadow-lg glow-button-active"
                      : "bg-gray-600 text-gray-300 hover-glow-md"
                  }`}
                  onClick={() => setProfitabilityView(view)}
                  style={
                    profitabilityView === view
                      ? { boxShadow: "0 8px 25px rgba(66, 176, 213, 0.5)" }
                      : {}
                  }
                >
                  AI {view.charAt(0).toUpperCase() + view.slice(1)}
                </motion.button>
              ))}
            </div> */}

            {/* AI Intelligence Display */}
            {/* <AnimatePresence mode="wait">
              <motion.div
                key={profitabilityView}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#42B0D5] mb-4 glow-text-large">
                  {profitabilityData[profitabilityView].value}
                </div>
                <div className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 glow-text-subtle">
                  {profitabilityData[profitabilityView].label}
                </div> */}

                {/* AI Trend Visualization */}
                {/* <div className="flex justify-center">
                  <div className="w-64 sm:w-80 h-32 sm:h-40 bg-gradient-to-r from-gray-800 to-gray-600 rounded-xl sm:rounded-2xl flex items-end justify-center p-4 sm:p-6 border border-gray-500 ai-chart">
                    {[...Array(10)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        animate={{ height: `${25 + Math.random() * 65}%` }}
                        transition={{ 
                          duration: 0.8, 
                          delay: i * 0.05,
                          ease: "easeOut"
                        }}
                        className="bg-[#42B0D5] w-4 sm:w-6 mx-1 rounded-t glow-bar"
                        style={{
                          boxShadow: "0 0 15px rgba(66, 176, 213, 0.6)",
                        }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section> */}

      {/* Trust & Fit */}
      {/* <section className="py-16 sm:py-24 bg-black text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 glow-text">
              Enterprise AI You Can Trust
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-8 sm:gap-12 text-center">
            {[
              {
                icon: Shield,
                title: "Enterprise AI security by design",
                description:
                  "Neural networks built with Maersk's security standards and compliance requirements.",
              },
              {
                icon: Globe,
                title: "Grounded in Maersk intelligence",
                description:
                  "AI trained on real Ocean and Inland operational data across the global network.",
              },
              {
                icon: Container,
                title: "Built for maritime AI operations",
                description:
                  "Neural intelligence designed specifically for Maersk's unique operational requirements.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="mb-6 flex justify-center">
                  <item.icon size={48} className="text-[#42B0D5] glow-icon-large" />
                </div>
                <h3 className="text-xl font-bold mb-4 glow-text-subtle">{item.title}</h3>
                <p className="text-gray-300 text-base">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Footer CTA */}
      <footer className="bg-gray-900 py-12 sm:py-16 border-t border-gray-700 scroll-mt-20">
        <div className="max-w-3xl mx-auto text-center px-4 sm:px-6">
          {/* KBC QUIZ EVENT BANNER (Temporary - Event Only) - moved from hero to footer */}
          <div className="mb-8 md:mb-12">
            <KBCEventBanner isAuthenticated={isAuthenticated} />
          </div>

          <motion.h2
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 glow-text"
          >
            Ready to experience AI-powered shipment intelligence?
          </motion.h2>

          <motion.button
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            whileHover={shouldReduceMotion ? {} : {
              scale: inProgress === InteractionStatus.None ? 1.05 : 1,
              boxShadow: inProgress === InteractionStatus.None ? "0 10px 25px rgba(66, 176, 213, 0.3)" : "none"
            }}
            whileTap={shouldReduceMotion ? {} : { scale: inProgress === InteractionStatus.None ? 0.95 : 1 }}
            onClick={handleStartExploring}
            disabled={inProgress !== InteractionStatus.None}
            className={`bg-gradient-to-r from-[#42B0D5] to-[#42B0D5] hover:from-[#42B0D5] hover:to-[#5FC0E8] text-white px-8 sm:px-12 py-3 sm:py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg flex items-center gap-3 mx-auto glow-button ${
              inProgress !== InteractionStatus.None ? 'opacity-60 cursor-not-allowed' : ''
            }`}
            style={{ 
              boxShadow: "0 8px 20px rgba(66, 176, 213, 0.3)",
              willChange: shouldReduceMotion ? 'auto' : 'transform'
            }}
          >
            <MessageCircle size={20} />
            {inProgress !== InteractionStatus.None ? 'Loading...' : 
             !FEATURE_FLAGS.REQUIRE_AUTHENTICATION ? 'Start Exploring' :
             isAuthenticated ? 'Continue to Chat' : 'Start Exploring'}
            <ArrowRight size={20} />
          </motion.button>

          {/* <div className="mt-12 text-gray-400">
            <p>© 2025 Maersk. AI-powered Ocean and Inland operations.</p>
          </div> */}
        </div>
      </footer>
    </div>
  );
}
